import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Input,
  Row,
  Card,
  Form,
  Col,
  Typography,
  Select,
  Button,
  Layout,
  Image,
  notification,
} from "antd";
import { isEmpty, isPlainObject, isUndefined } from "lodash";
import { useNavigate } from "react-router-dom";
import actions from "../../redux/donorDetails/actions";
import myDonationsActions from "../../redux/myDonations/actions";
import config from "../../Services/apiEndPoints";
import "../../Screens/form.css";

const { Option } = Select;
const { TextArea } = Input;
const { Header } = Layout;

export default function DonorPersonalDetails() {
  const [donorPersonalForm] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { successMessage, errorMessage, actionType } = useSelector(
    (state) => state.donor
  );
  const { myDonationsList: donorDetails } = useSelector(
    (state) => state.myDonations
  );

  const donorId = localStorage.getItem("donorId");
  const mobileNumber = localStorage.getItem("mobileNumber");

  const DonorTypesList = [{ label: "individual" }, { label: "organization" }];

  useEffect(() => {
    if (
      actionType === actions.DONOR_PERSONAL_DETAILS_CREATE_SUCCESS ||
      actionType === actions.DONOR_DETAILS_UPDATE_SUCCESS
    ) {
      notification.success({
        message: successMessage,
      });
      navigate("/DonationDetails");

      dispatch(actions.resetDonorPersonalDetails());
    } else if (
      actionType === actions.DONOR_PERSONAL_DETAILS_CREATE_ERROR ||
      actionType === actions.DONOR_DETAILS_UPDATE_ERROR
    ) {
      notification.error({
        message: errorMessage,
      });
      dispatch(actions.resetDonorPersonalDetails());
    }
  }, [actionType, successMessage, errorMessage]);

  useEffect(() => {
    if (!isEmpty(donorId) && !isUndefined(donorId)) {
      dispatch(
        myDonationsActions.getMyDonationsList(
          `${config.myDonationsList}?donorId=${donorId}`
        )
      );
    }
  }, []);

  useEffect(() => {
    if (isPlainObject(donorDetails) && !isEmpty(donorDetails)) {
      donorPersonalForm.setFieldsValue({
        ...donorDetails,
      });
    } else {
      donorPersonalForm.setFieldsValue({
        mobile: mobileNumber,
      });
    }
  }, [donorDetails]);

  const handleNext = async () => {
    try {
      const values = await donorPersonalForm.validateFields();
      if (!isEmpty(donorId) && !isUndefined(donorId)) {
        values["id"] = donorId;
        dispatch(actions.updateDonorDetails(config.donorUpdate, values));
      } else {
        dispatch(
          actions.createDonorPersonalDetails(config.donorCreate, values)
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    // Push the current state to history to prevent back navigation
    window.history.pushState(null, null, "", window.location.href);

    const handlePopState = () => {
      // This will force the user to stay on the current page
      window.history.pushState(null, null, "", window.location.href);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return (
    <div className="responsive-container">
      <Card className="premium-card" style={{ maxWidth: "800px" }}>
        <div className="form-header">
          <img
            src="/favicon.ico"
            alt="Logo"
            className="form-logo"
          />
          <Typography.Title level={2} style={{ margin: 0, color: "var(--text-main)" }}>
            Donor Details
          </Typography.Title>
          <Typography.Text type="secondary">
            Please provide your information to continue
          </Typography.Text>
        </div>

        <Form
          form={donorPersonalForm}
          layout="vertical"
          requiredMark="optional"
        >
          <Row gutter={[24, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: "Name is required." }]}
              >
                <Input
                  placeholder="Enter full name"
                  className="custom-input"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Email is required." },
                  {
                    type: "email",
                    message: "Please enter a valid email address.",
                  },
                ]}
              >
                <Input
                  placeholder="Enter your email"
                  className="custom-input"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="pan_number"
                label="PAN Number"
                rules={[
                  {
                    required: true,
                    message: "PAN number is required.",
                  },
                  {
                    validator: (_, value) => {
                      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
                      return !value || panRegex.test(value)
                        ? Promise.resolve()
                        : Promise.reject("Please enter a valid PAN number.");
                    },
                  },
                ]}
              >
                <Input
                  placeholder="ABCDE1234F"
                  className="custom-input"
                  disabled={!isEmpty(donorDetails) && donorDetails.pan_number}
                  maxLength={10}
                  style={{ textTransform: "uppercase" }}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    donorPersonalForm.setFieldsValue({ pan_number: value });
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="mobile"
                label="Mobile Number"
              >
                <Input
                  className="custom-input"
                  disabled={true}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="donor_type"
                label="Donor Type"
                initialValue={"individual"}
              >
                <Select
                  className="custom-select"
                  placeholder="Select type"
                >
                  {DonorTypesList.map((item, index) => (
                    <Option key={index} value={item.label}>
                      {item.label.charAt(0).toUpperCase() + item.label.slice(1)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Typography.Title level={5} style={{ marginBottom: 16, marginTop: 8 }}>
                Address Information
              </Typography.Title>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label="Address Line 1"
                name={["address", "address1"]}
                rules={[{ required: true, message: "Address is required." }]}
              >
                <Input
                  placeholder="Building No, Street Name"
                  className="custom-input"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Address Line 2" name={["address", "address2"]}>
                <Input
                  placeholder="Area, Locality"
                  className="custom-input"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item label="Address Line 3" name={["address", "address3"]}>
                <Input
                  placeholder="Landmark"
                  className="custom-input"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name={["address", "city"]}
                label="City"
                rules={[{ required: true, message: "City is required." }]}
              >
                <Input placeholder="Enter City" className="custom-input" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name={["address", "pincode"]}
                label="Pincode"
                rules={[
                  { required: true, message: "Pincode is required." },
                  { len: 6, message: "Must be 6 digits." },
                ]}
              >
                <Input
                  placeholder="000000"
                  className="custom-input"
                  type="number"
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={handleNext}
              className="premium-button"
              style={{ width: "160px" }}
            >
              Next Step
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
}
